/**
 * What an OBJECT is in a GLB model, and where a device anchored to one sits
 * (card t_cb0aa19b).
 *
 * A glTF has no semantic tree — no expressID, no storeys, no element types. Its
 * objects are its NODES, and the only identity the file itself gives them is the
 * node's name … plus, when the exporter wrote one, the `extras` bag a GLTFLoader
 * hands back as `userData`. The demo-house exporter
 * (`otto-design/…/tools/export-glb.mjs`) writes `userData.expressID` per mesh,
 * so ITS GLBs carry the IFC element id through the round trip while a RoomPlan
 * GLB carries nothing but names (`Wall0`, `Window2`). Both must work.
 *
 * Hence ONE key rule, total and stable:
 *
 *   · a node whose `extras` carried an integer expressID is `ifc:<id>` — the
 *     IFC element's own identity, which survives a re-export of the same model
 *     (ids are assigned by the source, not by the export) and merges the
 *     several meshes one element may be split into by material;
 *   · otherwise the node's own trimmed name is `name:<name>` — the only
 *     identity a scan's GLB has. Two nodes sharing a name ARE one object to
 *     this app (the file's own ambiguity: a GLB has no other handle, and hiding
 *     "the object called Wall" is exactly what the name means);
 *   · a node with neither (an unnamed mesh and no id) is not an object — it is
 *     counted and left alone, because there is nothing to name it by.
 *
 * The KEY is the identity — it is what the hidden-object set, a view preset and
 * a widget's anchor store. The row's `name` is a label for the panel and may be
 * re-flowed by a later export without breaking a binding.
 *
 * The ANCHOR is the same idea one step further: a device planted on a GLB
 * surface stores the object it was planted on plus the hit point in that
 * object's OWN local frame and the surface normal in world space. Storing the
 * world point alone would tie the device to coordinates; storing the frame
 * makes the point follow the object it belongs to (see `anchorWorldPoint`).
 *
 * Pure by design — no three, no Vue, no scene — so the rules are plain Node
 * unit tests (`bimObjects.test.ts`) and the rigs can drive every case. The
 * three.js side (walking a loaded scene, hiding meshes) lives in
 * `composables/useBimScene.ts`.
 */
import { field, isWireObject, wireArray, wireNumber, wireString } from './wire';
import type { JsonRecord } from '@hub/types/json';

/** The largest expressID an IFC line number may be (the schema's own cap). */
export const MAX_BIM_EXPRESS_ID = 2147483647;

/** A node key is at most this long (the hub's JSON is generous; names are not). */
export const MAX_BIM_OBJECT_KEY_CHARS = 240;

/** How an object's identity was decided — the file's own evidence. */
export type BimObjectKind = 'ifc' | 'name';

/** One object of a GLB model: its identity, its label and where it sits. */
export interface BimObjectInfo {
  /** The object's own identity (`ifc:<id>` / `name:<name>`) — what a hide, a
   *  preset and an anchor store. */
  key: string;
  /** The name the FILE gives it, for the panel. */
  name: string;
  /** Which evidence produced the key. */
  kind: BimObjectKind;
  /** The IFC id the node's `extras` carried, when it did (the muted second label). */
  expressId: number | null;
  /** Depth below the model's own root (the root's children are 0). */
  depth: number;
  /** The nearest NAMED ancestor's name, when there is one. */
  parent: string | null;
  /** How many meshes the object owns (its own + its subtree's). */
  meshCount: number;
}

/**
 * What one node of a loaded glTF contributes to the object list, before the
 * merge: the scene walk's own facts. `name` is the node's name as the walk sees
 * it — the loader's own spelling, which is what keys the object (`''` when
 * unnamed) — and `label` is the spelling the FILE gave the node when the loader
 * kept one (three's GLTFLoader rewrites the node name — whitespace becomes `_`
 * and a repeated name gets a `_1` suffix — so `name` is the stable identity and
 * `label` is what a human should read). `expressId` is the node's
 * `userData.expressID` when that is a usable IFC id.
 */
export interface BimObjectNodeFacts {
  name: string;
  /** The file's own spelling, when the loader kept it (the row's label). */
  label?: string | null;
  expressId: number | null;
  depth: number;
  parent: string | null;
  meshCount: number;
}

/** The trimmed name a node may be identified by, or null when it has none. */
function usableName(value: unknown): string | null {
  const name = wireString(value)?.trim() ?? '';
  if (!name) return null;
  if (name.length > MAX_BIM_OBJECT_KEY_CHARS) return null;
  return name;
}

/** A usable IFC express id (the edits contract's own range), or null. */
export function usableExpressId(value: unknown): number | null {
  const id = wireNumber(value);
  if (id === undefined) return null;
  if (!Number.isInteger(id) || id < 1 || id > MAX_BIM_EXPRESS_ID) return null;
  return id;
}

/**
 * The object key for one node: the id when the node carried one, the name
 * otherwise, and `null` when the node gives neither.
 */
export function bimObjectKeyOf(name: unknown, expressId: unknown): string | null {
  const id = usableExpressId(expressId);
  if (id !== null) return `ifc:${id}`;
  const clean = usableName(name);
  return clean === null ? null : `name:${clean}`;
}

/** The two halves of a key, or null when the string is not one. */
export function bimObjectKeyParts(key: unknown): { kind: BimObjectKind; payload: string } | null {
  const raw = wireString(key)?.trim() ?? '';
  if (raw.length === 0 || raw.length > MAX_BIM_OBJECT_KEY_CHARS) return null;
  if (raw.startsWith('ifc:')) {
    const id = raw.slice(4);
    return /^[1-9]\d*$/.test(id) ? { kind: 'ifc', payload: id } : null;
  }
  if (raw.startsWith('name:')) {
    const name = raw.slice(5);
    return name.length > 0 ? { kind: 'name', payload: name } : null;
  }
  return null;
}

/**
 * What to show for a key that this revision does not carry (a widget anchored
 * to an object the current GLB has lost): the name for a name-key, the bare id
 * for an id-key — never the raw `name:` / `ifc:` spelling.
 */
export function bimObjectKeyLabelOf(key: unknown): string {
  const parts = bimObjectKeyParts(key);
  return parts === null ? '' : parts.payload;
}

/**
 * The panel's rows: one per OBJECT, merged from the nodes that share a key
 * (an IFC element split into several material buckets is one object; two nodes
 * with one name are one object by the rule above).
 *
 * The first fact of a key owns the row's label and position in the tree; the
 * mesh counts ADD UP, because the row is the whole object. The label is the
 * FILE's own spelling when the walk carried one (`fact.label`) and the key's
 * payload otherwise — the key is the identity and the label is only ever read
 * by a human, so a loader that rewrote the name (`Fasadni_zid_(pritličje)`) still
 * shows what the file says (`Fasadni zid (pritličje)`). Insertion order is kept
 * — the walk is a depth-first pass of the file's own tree, which is the order a
 * human reads a hierarchy in — and rows with no usable key are dropped (there is
 * nothing to name them by; the scene logs how many).
 */
export function bimObjectRows(facts: BimObjectNodeFacts[]): BimObjectInfo[] {
  const rows = new Map<string, BimObjectInfo>();
  for (const fact of facts) {
    const key = bimObjectKeyOf(fact.name, fact.expressId);
    if (key === null) continue;
    const parts = bimObjectKeyParts(key)!;
    const existing = rows.get(key);
    if (existing) {
      existing.meshCount += fact.meshCount;
      continue;
    }
    rows.set(key, {
      key,
      name: usableName(fact.label) ?? usableName(fact.name) ?? parts.payload,
      kind: parts.kind,
      expressId: parts.kind === 'ifc' ? Number(parts.payload) : null,
      depth: Number.isFinite(fact.depth) && fact.depth > 0 ? Math.floor(fact.depth) : 0,
      parent: usableName(fact.parent),
      meshCount: Math.max(0, Math.floor(fact.meshCount)),
    });
  }
  return [...rows.values()];
}

// ─── The anchor ──────────────────────────────────────────────────────────────

/**
 * Where a device widget sits on a GLB object: the object it was planted on, the
 * hit point in that object's own LOCAL frame (metres) and the surface normal in
 * WORLD space (a unit vector — the label's orientation).
 *
 * Stored in the widget's config under `bimAnchor`, in the same free-form bag the
 * IFC path's `ifcElementId` rides, so the hub needs no contract change.
 */
export interface BimObjectAnchor {
  /** The object key (`ifc:<id>` / `name:<name>`). */
  object: string;
  /** The hit point in the object's local frame, metres. */
  local: [number, number, number];
  /** The surface normal at the hit, world space. */
  normal: [number, number, number];
}

/** The widget-config key the anchor lives under. */
export const BIM_ANCHOR_CONFIG_KEY = 'bimAnchor';

function triple(value: unknown): [number, number, number] | null {
  const items = wireArray(value);
  if (!items || items.length < 3) return null;
  const out: number[] = [];
  for (let i = 0; i < 3; i++) {
    const n = wireNumber(items[i]);
    if (n === undefined) return null;
    out.push(n);
  }
  return [out[0], out[1], out[2]];
}

/**
 * Read any JSON into an anchor, or `null` when it is not one. Total by contract:
 * a widget the hub hands back with a mangled or half-written anchor reads as
 * "no anchor" (the widget's own stored point stands) rather than as a throw — a
 * failed read must not take a view down.
 */
export function parseBimObjectAnchor(raw: unknown): BimObjectAnchor | null {
  if (!isWireObject(raw)) return null;
  const object = wireString(field(raw, 'object'))?.trim() ?? '';
  if (bimObjectKeyParts(object) === null) return null;
  const local = triple(field(raw, 'local'));
  const normal = triple(field(raw, 'normal'));
  if (!local || !normal) return null;
  return { object, local, normal };
}

/** The anchor inside a widget config, or null when the widget carries none. */
export function bimAnchorOf(config: unknown): BimObjectAnchor | null {
  if (!isWireObject(config)) return null;
  return parseBimObjectAnchor(field(config, BIM_ANCHOR_CONFIG_KEY));
}

/** The anchor as the JSON a config column may carry. */
export function serializeBimObjectAnchor(anchor: BimObjectAnchor): JsonRecord {
  return {
    object: anchor.object,
    local: [anchor.local[0], anchor.local[1], anchor.local[2]],
    normal: [anchor.normal[0], anchor.normal[1], anchor.normal[2]],
  };
}

/**
 * The world point of a local point under a column-major 4×4 matrix — three's own
 * `Matrix4.elements` layout, so `object.matrixWorld.elements` is what a caller
 * passes. The `w` row is ignored: every matrix this app hands over is affine
 * (a scene node's world matrix), which is the case the anchor is defined for.
 */
export function anchorWorldPoint(
  local: readonly [number, number, number],
  matrix: ArrayLike<number>,
): { x: number; y: number; z: number } {
  const [x, y, z] = local;
  const m = matrix;
  return {
    x: m[0] * x + m[4] * y + m[8] * z + m[12],
    y: m[1] * x + m[5] * y + m[9] * z + m[13],
    z: m[2] * x + m[6] * y + m[10] * z + m[14],
  };
}

/**
 * How far a widget anchored to an object sits from the point the anchor resolves
 * to now — the re-seat's own test. Below this the stored point is left exactly
 * as it is (a re-export that moved nothing must not rewrite a single widget).
 */
export const BIM_ANCHOR_RESEAT_EPSILON_M = 0.02;

/** Distance between two world points, metres. */
export function anchorDistance(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
